/**
 * --------------------------------------------------------------------------
 * Liander Bootstrap main-nav.js
 * --------------------------------------------------------------------------
 */

/**
 * Constants
 */

import {
  defineJQueryPlugin,
  isVisible
} from '../../node_modules/bootstrap/js/src/util/index.js'
import EventHandler from '../../node_modules/bootstrap/js/src/dom/event-handler.js'
import SelectorEngine from '../../node_modules/bootstrap/js/src/dom/selector-engine.js'
import BaseComponent from '../../node_modules/bootstrap/js/src/base-component.js'

const NAME = 'mainnav'
const DATA_KEY = 'lbs.main-nav'
const EVENT_KEY = `.${DATA_KEY}`
const DATA_API_KEY = '.data-api'

const EVENT_LOAD_DATA_API = `load${EVENT_KEY}${DATA_API_KEY}`
const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`

const CLASS_NAME_STICKY = 'sticky'

const SELECTOR_MENU = '.main-nav'
const SELECTOR_MENU_LEVEL_2 = '.main-nav-level-2'
const SELECTOR_DUMMY = '.main-nav-dummy'
const SELECTOR_MOBILE_MENU = '.main-nav-mobile'
const SELECTOR_MOBILE_MENU_TOGGLE = '.main-nav-mobile-toggle'
const SELECTOR_OVERLAY = '.main-nav-overlay'
const SELECTOR_SUB_MENU_TOGGLE = '.sub-menu-toggle'
const SELECTOR_SUB_MENU_TOGGLE_ICON = '.sub-menu-toggle-icon'
const SELECTOR_MOBILE_SEARCH = '.main-nav-mobile-search'
const SELECTOR_MOBILE_SEARCH_TOGGLE = '.main-nav-mobile-search-toggle'

const Default = {}

const DefaultType = {}

const GAP = 16

/**
 * Class definition
 */

class MainNav extends BaseComponent {
  constructor(element, config) {
    super(element, config)

    this._menuLevel2Offset = {}

    this._setPositions()
    this._addEventListeners()
  }

  // Getters
  static get Default() {
    return Default
  }

  static get DefaultType() {
    return DefaultType
  }

  static get NAME() {
    return NAME
  }

  // Public
  toggle() {
    this._element.classList.toggle('open')
    this._getOverlay().classList.toggle('hidden')
  }

  show() {
    this._element.classList.add('open')
    this._getOverlay().classList.remove('hidden')
  }

  hide() {
    this._element.classList.remove('open')
    this._getOverlay().classList.add('hidden')
  }

  toggleSubMenu(target) {
    const lis = SelectorEngine.parents(target, 'li')
    for (const li of lis) {
      li.classList.toggle('expanded')
      li.classList.toggle('collapsed')
    }

    const subMenuToggleIcon = SelectorEngine.findOne(SELECTOR_SUB_MENU_TOGGLE_ICON, target)
    subMenuToggleIcon.classList.toggle('fa-angle-down')
    subMenuToggleIcon.classList.toggle('fa-angle-up')
  }

  toggleSearch() {
    const mobileSearch = SelectorEngine.findOne(SELECTOR_MOBILE_SEARCH)
    mobileSearch.classList.toggle('open')
  }

  // Private
  _addEventListeners() {
    window.addEventListener('scroll', () => {
      this._setPositions()
    })
  }

  _getDummy() {
    return SelectorEngine.findOne(SELECTOR_DUMMY)
  }

  _getMobileMenu() {
    return SelectorEngine.findOne(SELECTOR_MOBILE_MENU)
  }

  _getOverlay() {
    return SelectorEngine.findOne(SELECTOR_OVERLAY)
  }

  _setPositions() {
    if (this._inMobileMode()) {
      this._setDummyHeightTo(SELECTOR_MOBILE_MENU)
    } else {
      if (!this._menuIsSticky()) {
        this._initLevel2Offset()
        this._setDummyHeightTo(SELECTOR_MENU)
      }

      this._doStickyCheck()
    }
  }

  _initLevel2Offset() {
    const menuLevel2 = SelectorEngine.findOne(SELECTOR_MENU_LEVEL_2, this._element)
    this._menuLevel2Offset = {
      top: menuLevel2.offsetTop - GAP,
      left: menuLevel2.offsetLeft
    }
  }

  _setDummyHeightTo(selector) {
    const element = SelectorEngine.findOne(selector)
    this._getDummy().style.height = `${element.offsetHeight}px`
  }

  _doStickyCheck() {
    if (!this._menuLevel2Offset) {
      return
    }

    if (window.scrollY >= this._menuLevel2Offset.top) {
      this._element.classList.add(CLASS_NAME_STICKY)
      this._getDummy().classList.add(CLASS_NAME_STICKY)
    } else {
      this._element.classList.remove(CLASS_NAME_STICKY)
      this._getDummy().classList.remove(CLASS_NAME_STICKY)
    }
  }

  _menuIsSticky() {
    return this._element.classList.contains(CLASS_NAME_STICKY)
  }

  _inMobileMode() {
    return isVisible(this._getMobileMenu())
  }

  // Static
  static jQueryInterface(config) {
    return this.each(function () {
      const data = MainNav.getOrCreateInstance(this, config)

      if (typeof config === 'string') {
        if (data[config] === undefined || config.startsWith('_') || config === 'constructor') {
          throw new TypeError(`No method named "${config}"`)
        }

        data[config]()
      }
    })
  }
}

/**
 * Data API implementation
 */

EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_MOBILE_MENU_TOGGLE, () => {
  const element = SelectorEngine.findOne(SELECTOR_MENU)
  MainNav.getOrCreateInstance(element).toggle()
})

EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_OVERLAY, () => {
  const element = SelectorEngine.findOne(SELECTOR_MENU)
  MainNav.getOrCreateInstance(element).hide()
})

EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_SUB_MENU_TOGGLE, event => {
  const element = SelectorEngine.findOne(SELECTOR_MENU)
  MainNav.getOrCreateInstance(element).toggleSubMenu(event.delegateTarget)
})

EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_MOBILE_SEARCH_TOGGLE, () => {
  const element = SelectorEngine.findOne(SELECTOR_MENU)
  MainNav.getOrCreateInstance(element).toggleSearch()
})

EventHandler.on(window, EVENT_LOAD_DATA_API, () => {
  const mainNav = SelectorEngine.findOne(SELECTOR_MENU)
  if (mainNav) {
    MainNav.getOrCreateInstance(mainNav)
  }
})

/**
 * jQuery
 */

defineJQueryPlugin(MainNav)

export default MainNav
